import { useEffect } from "react";
import confetti from "canvas-confetti";
import { Check, ArrowRight } from "lucide-react";
import { dictionary, Language } from "../../../utils/dictionary";

const FLUXI_LOGO = "https://fluxi.s3.amazonaws.com/prod/assets/fluxi/fluxi-logo.svg";

interface ModalSuccessIntegrationProps {
    isOpen: boolean;
    onClose: () => void;
    integrationName: string;
    integrationLogo: string;
    language: Language;
    integrationType?: string | null;
}

const chatChannelDescriptions: Record<string, Record<Language, string>> = {
    whatsapp: {
        [Language.SPANISH]: "se ha vinculado correctamente. A partir de ahora podrás gestionar mensajes y conversaciones desde",
        [Language.ENGLISH]: "has been connected successfully. From now on, you can manage messages and conversations from",
        [Language.PORTUGUESE]: "foi vinculado corretamente. A partir de agora, você poderá gerenciar mensagens e conversas pelo",
    },
    messenger: {
        [Language.SPANISH]: "se ha vinculado correctamente. A partir de ahora podrás gestionar mensajes y conversaciones desde",
        [Language.ENGLISH]: "has been connected successfully. From now on, you can manage messages and conversations from",
        [Language.PORTUGUESE]: "foi vinculado corretamente. A partir de agora, você poderá gerenciar mensagens e conversas pelo",
    },
    instagram: {
        [Language.SPANISH]: "se ha vinculado correctamente. A partir de ahora podrás gestionar DMs y conversaciones desde",
        [Language.ENGLISH]: "has been connected successfully. From now on, you can manage DMs and conversations from",
        [Language.PORTUGUESE]: "foi vinculado corretamente. A partir de agora, você poderá gerenciar DMs e conversas pelo",
    },
};

const ModalSuccessIntegration = ({
    isOpen,
    onClose,
    integrationName,
    integrationLogo,
    language,
    integrationType,
}: ModalSuccessIntegrationProps) => {

    useEffect(() => {
        if (!isOpen) return;

        const duration = 3000;
        const animationEnd = Date.now() + duration;
        const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 60 };

        const randomInRange = (min: number, max: number) => Math.random() * (max - min) + min;

        const interval = setInterval(() => {
            const timeLeft = animationEnd - Date.now();
            if (timeLeft <= 0) {
                clearInterval(interval);
                return;
            }

            const particleCount = 50 * (timeLeft / duration);

            confetti({
                ...defaults,
                particleCount,
                origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
            });
            confetti({
                ...defaults,
                particleCount,
                origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
            });
        }, 250);

        return () => clearInterval(interval);
    }, [isOpen]);

    if (!isOpen) return null;

    const successDescription =
        (integrationType ? chatChannelDescriptions[integrationType]?.[language] : null) ||
        dictionary["integration-success-description"]?.[language] ||
        "se ha vinculado correctamente. A partir de ahora, todo tu inventario y órdenes se sincronizarán en tiempo real con";

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm transition-all duration-300">
            <div className="bg-gradient-to-b from-white to-[#f0f6ff] rounded-[32px] shadow-2xl w-full max-w-[480px] p-8 flex flex-col items-center text-center relative border border-blue-50">

                {/* Success Icon */}
                <div className="w-16 h-16 bg-[#00c68a] rounded-full flex items-center justify-center mb-6 shadow-lg shadow-green-100 ring-4 ring-green-50">
                    <Check className="text-white w-8 h-8" strokeWidth={3} />
                </div>

                {/* Title */}
                <h2 className="text-2xl font-bold text-gray-900 mb-3">
                    {dictionary["integration-success-title"]?.[language] || "Integración Exitosa!"}
                </h2>

                {/* Description */}
                <p className="text-gray-500 text-[15px] leading-relaxed mb-8 max-w-[360px]">
                    <span className="capitalize">{integrationName}</span>{" "}
                    {successDescription}{" "}
                    <span className="font-semibold text-gray-900">Fluxi</span>.
                </p>

                {/* Integration Visual - Cards */}
                <div className="flex items-center gap-6 mb-10">
                    {/* Fluxi Card */}
                    <div className="bg-white rounded-[20px] shadow-[0_4px_20px_rgba(59,130,246,0.15)] border border-blue-100 w-20 h-20 flex items-center justify-center">
                        <img src={FLUXI_LOGO} alt="Fluxi" className="w-16 h-auto object-contain" />
                    </div>

                    {/* Connection Line */}
                    <div className="relative w-12 h-[2px] bg-blue-100 overflow-hidden rounded-full">
                        <div className="absolute top-0 left-0 w-full h-full bg-blue-500 animate-[successFlow_1.5s_linear_infinite] origin-left scale-x-50" />
                    </div>
                    <style>{`
                        @keyframes successFlow {
                            0% { transform: translateX(-100%); }
                            100% { transform: translateX(100%); }
                        }
                    `}</style>

                    {/* Integration Card */}
                    <div className="bg-white rounded-[20px] shadow-sm border border-gray-200 w-20 h-20 flex items-center justify-center">
                        <img src={integrationLogo} alt={integrationName} className="w-10 h-10 object-contain" />
                    </div>
                </div>

                {/* Button */}
                <button
                    onClick={onClose}
                    className="w-full bg-[#3b82f6] hover:bg-[#2563eb] text-white font-semibold py-3.5 rounded-xl transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5 flex items-center justify-center gap-2 group"
                >
                    {dictionary["continue"]?.[language] || "Continuar"}
                    <ArrowRight size={18} className="opacity-80 group-hover:opacity-100 group-hover:translate-x-0.5 transition-transform" />
                </button>
            </div>
        </div>
    );
};

export default ModalSuccessIntegration;
